import { clusterRobustStandardErrors, fitOls, fitSimpleOls, median, quantile } from "./math";
import { available, notAvailable, type MetricResult } from "./types";
import {
  compareISODate,
  getPairPreset,
  parseISODate,
  type ActualContractPair,
  type EnabledCanolaMonthCode,
  type ISODate,
  type PairPresetId,
} from "../domain";

export type ApproximateProbabilityBand = "<0.01" | "0.01-0.05" | "0.05-0.10" | ">=0.10";

export interface UnitRootEstimate {
  readonly statistic: number;
  readonly lags: number;
  readonly criticalValues: Readonly<{ "1%": number; "5%": number; "10%": number }>;
  readonly approximateProbabilityBand: ApproximateProbabilityBand;
  readonly exactPValue: null;
  readonly rejectAt: "1%" | "5%" | "10%" | "none";
  readonly deterministicTerms: "constant";
  readonly caveat: string;
}

const ADF_CRITICAL_VALUES = { "1%": -3.43, "5%": -2.86, "10%": -2.57 } as const;
const EG_CRITICAL_VALUES = { "1%": -3.9, "5%": -3.34, "10%": -3.04 } as const;

function classifyStatistic(
  statistic: number,
  criticalValues: Readonly<{ "1%": number; "5%": number; "10%": number }>,
): Pick<UnitRootEstimate, "approximateProbabilityBand" | "rejectAt"> {
  if (statistic < criticalValues["1%"]) return { approximateProbabilityBand: "<0.01", rejectAt: "1%" };
  if (statistic < criticalValues["5%"]) return { approximateProbabilityBand: "0.01-0.05", rejectAt: "5%" };
  if (statistic < criticalValues["10%"]) return { approximateProbabilityBand: "0.05-0.10", rejectAt: "10%" };
  return { approximateProbabilityBand: ">=0.10", rejectAt: "none" };
}

function unitRootRegression(
  values: readonly number[],
  lags: number,
): { statistic: number; sampleSize: number } | null {
  const delta = values.map((value, index) => index === 0 ? Number.NaN : value - (values[index - 1] ?? value));
  const dependent: number[] = [];
  const design: number[][] = [];
  for (let index = lags + 1; index < values.length; index += 1) {
    const currentDelta = delta[index];
    const laggedLevel = values[index - 1];
    if (currentDelta === undefined || laggedLevel === undefined) continue;
    const row = [1, laggedLevel];
    for (let lag = 1; lag <= lags; lag += 1) row.push(delta[index - lag] ?? Number.NaN);
    dependent.push(currentDelta);
    design.push(row);
  }
  const fit = fitOls(dependent, design);
  const coefficient = fit?.coefficients[1];
  const standardError = fit?.standardErrors[1];
  if (!fit || coefficient === undefined || standardError === undefined || standardError <= 0) return null;
  return { statistic: coefficient / standardError, sampleSize: fit.sampleSize };
}

/**
 * Low-level estimator for already provenance-validated values. Formal callers
 * must first enforce actual-episode/seasonal-panel scope and reject QA or
 * back-adjusted continuous inputs.
 */
export function augmentedDickeyFuller(
  values: readonly number[],
  options: { readonly lags?: number; readonly minimumSample?: number } = {},
): MetricResult<UnitRootEstimate> {
  const lags = options.lags ?? 1;
  const minimumSample = options.minimumSample ?? 30;
  if (!Number.isInteger(lags) || lags < 0) return notAvailable("invalid-input", "ADF lags must be non-negative.", values.length);
  if (values.length < minimumSample) {
    return notAvailable("insufficient-sample", `ADF requires at least ${minimumSample} observations.`, values.length);
  }
  if (values.some((value) => !Number.isFinite(value))) {
    return notAvailable("invalid-input", "ADF input contains a non-finite value.", values.length);
  }
  const estimate = unitRootRegression(values, lags);
  if (!estimate) return notAvailable("unstable-estimate", "ADF regression is singular.", values.length);
  const classification = classifyStatistic(estimate.statistic, ADF_CRITICAL_VALUES);
  return available(
    {
      statistic: estimate.statistic,
      lags,
      criticalValues: ADF_CRITICAL_VALUES,
      ...classification,
      exactPValue: null,
      deterministicTerms: "constant",
      caveat: "Critical values are approximate asymptotic ADF values; no exact MacKinnon p-value is claimed.",
    },
    estimate.sampleSize,
    "ADF regression Δx_t = c + γx_(t-1) + lagged Δx; approximate critical-value classification",
  );
}

export interface EngleGrangerEstimate extends UnitRootEstimate {
  readonly alpha: number;
  readonly beta: number;
  readonly rSquared: number;
  readonly firstStage: "OLS log-price levels with intercept";
}

export function engleGrangerCointegration(
  legA: readonly number[],
  legB: readonly number[],
  options: { readonly lags?: number; readonly minimumSample?: number } = {},
): MetricResult<EngleGrangerEstimate> {
  const minimumSample = options.minimumSample ?? 30;
  const lags = options.lags ?? 1;
  if (legA.length !== legB.length || legA.length < minimumSample) {
    return notAvailable("insufficient-sample", `Engle-Granger requires ${minimumSample} aligned observations.`, Math.min(legA.length, legB.length));
  }
  if (legA.some((value) => value <= 0 || !Number.isFinite(value)) || legB.some((value) => value <= 0 || !Number.isFinite(value))) {
    return notAvailable("invalid-input", "Engle-Granger log-price inputs must be finite and positive.", legA.length);
  }
  const firstStage = fitSimpleOls(legB.map(Math.log), legA.map(Math.log));
  if (!firstStage) return notAvailable("unstable-estimate", "Cointegrating OLS regression is singular.", legA.length);
  const unitRoot = unitRootRegression(firstStage.residuals, lags);
  if (!unitRoot) return notAvailable("unstable-estimate", "Residual unit-root regression is singular.", legA.length);
  const classification = classifyStatistic(unitRoot.statistic, EG_CRITICAL_VALUES);
  return available(
    {
      statistic: unitRoot.statistic,
      lags,
      criticalValues: EG_CRITICAL_VALUES,
      ...classification,
      exactPValue: null,
      deterministicTerms: "constant",
      caveat: "Two-series Engle-Granger residual critical values are approximate; no exact p-value is claimed, especially for short futures episodes.",
      alpha: firstStage.alpha,
      beta: firstStage.beta,
      rSquared: firstStage.rSquared,
      firstStage: "OLS log-price levels with intercept",
    },
    unitRoot.sampleSize,
    "Engle-Granger two-step test with approximate residual-based critical values",
  );
}

export interface HalfLifeEstimate {
  readonly phi: number;
  readonly gamma: number;
  readonly gammaTStatistic: number;
  readonly phiConfidenceInterval95: readonly [number, number];
  readonly halfLife: number;
  readonly significanceRule: "one-sided-5-percent";
}

export interface HalfLifeOptions {
  readonly minimumSample?: number;
  readonly analysisWindow?: number;
  readonly stationaritySupported?: boolean;
  readonly maximumPhiConfidenceWidth?: number;
}

export function estimateAr1HalfLife(
  residual: readonly number[],
  options: HalfLifeOptions = {},
): MetricResult<HalfLifeEstimate> {
  const minimumSample = options.minimumSample ?? 30;
  const analysisWindow = options.analysisWindow ?? residual.length;
  if (residual.length < minimumSample) {
    return notAvailable("insufficient-sample", `AR(1) half-life requires at least ${minimumSample} observations.`, residual.length);
  }
  if (residual.some((value) => !Number.isFinite(value))) {
    return notAvailable("invalid-input", "AR(1) input contains a non-finite value.", residual.length);
  }
  const lagged = residual.slice(0, -1);
  const current = residual.slice(1);
  const fit = fitSimpleOls(lagged, current);
  const standardError = fit?.standardErrors[1];
  if (!fit || standardError === undefined || standardError <= 0) {
    return notAvailable("unstable-estimate", "AR(1) regression is singular.", lagged.length);
  }
  const phi = fit.beta;
  const gamma = phi - 1;
  const gammaTStatistic = gamma / standardError;
  const confidenceInterval = [phi - 1.96 * standardError, phi + 1.96 * standardError] as const;
  if (phi <= 0 || phi >= 1) {
    return notAvailable("half-life-not-defined", `Simple OU interpretation requires 0 < phi < 1; estimated phi=${phi.toFixed(4)}.`, lagged.length);
  }
  if (gammaTStatistic >= -1.645) {
    return notAvailable("non-mean-reverting", "Mean-reversion coefficient is not significantly negative at the one-sided 5% level.", lagged.length);
  }
  if (options.stationaritySupported !== true) {
    return notAvailable("stationarity-not-supported", "AR(1) half-life requires an explicit residual-stationarity gate.", lagged.length);
  }
  const confidenceWidth = confidenceInterval[1] - confidenceInterval[0];
  if (confidenceWidth > (options.maximumPhiConfidenceWidth ?? 0.8)) {
    return notAvailable("wide-confidence-interval", "AR(1) persistence confidence interval is too wide for a defensible half-life.", lagged.length);
  }
  const halfLife = -Math.log(2) / Math.log(phi);
  if (!Number.isFinite(halfLife) || halfLife <= 0) {
    return notAvailable("half-life-not-defined", "AR(1) estimate does not imply a positive finite half-life.", lagged.length);
  }
  if (halfLife > analysisWindow) {
    return notAvailable("half-life-exceeds-window", "Estimated half-life is longer than the analysis window.", lagged.length);
  }
  return available(
    {
      phi,
      gamma,
      gammaTStatistic,
      phiConfidenceInterval95: confidenceInterval,
      halfLife,
      significanceRule: "one-sided-5-percent",
    },
    lagged.length,
    "AR(1) with intercept; half-life = -ln(2)/ln(phi), reported only after validity gates",
  );
}

export interface EpisodeStationarityEstimate {
  readonly contractYear: number;
  readonly beta?: number;
  readonly engleGrangerRejected?: boolean;
  readonly adfRejected?: boolean;
  readonly halfLife?: number;
  readonly meanReversionCoefficientNegative?: boolean;
}

export interface HistoricalStability {
  readonly scope: "historical-stability";
  readonly validContractYears: number;
  readonly engleGranger: { readonly rejected: number; readonly evaluated: number; readonly rate: number | null };
  readonly adf: { readonly rejected: number; readonly evaluated: number; readonly rate: number | null };
  readonly beta: { readonly median: number | null; readonly iqr: readonly [number, number] | null };
  readonly halfLife: { readonly median: number | null; readonly iqr: readonly [number, number] | null };
  readonly negativeMeanReversionYears: { readonly count: number; readonly evaluated: number; readonly rate: number | null };
}

function booleanRate(values: readonly (boolean | undefined)[]): { rejected: number; evaluated: number; rate: number | null } {
  const evaluated = values.filter((value): value is boolean => value !== undefined);
  const rejected = evaluated.filter(Boolean).length;
  return { rejected, evaluated: evaluated.length, rate: evaluated.length > 0 ? rejected / evaluated.length : null };
}

function distribution(values: readonly number[]): { median: number | null; iqr: readonly [number, number] | null } {
  const finite = values.filter(Number.isFinite);
  return finite.length > 0
    ? { median: median(finite), iqr: [quantile(finite, 0.25), quantile(finite, 0.75)] }
    : { median: null, iqr: null };
}

export function summarizeHistoricalStability(
  estimates: readonly EpisodeStationarityEstimate[],
): HistoricalStability {
  const engleGranger = booleanRate(estimates.map((estimate) => estimate.engleGrangerRejected));
  const adf = booleanRate(estimates.map((estimate) => estimate.adfRejected));
  const negative = booleanRate(estimates.map((estimate) => estimate.meanReversionCoefficientNegative));
  const validYears = estimates.filter((estimate) =>
    (estimate.beta !== undefined && Number.isFinite(estimate.beta)) ||
    estimate.engleGrangerRejected !== undefined ||
    estimate.adfRejected !== undefined ||
    (estimate.halfLife !== undefined && Number.isFinite(estimate.halfLife)) ||
    estimate.meanReversionCoefficientNegative !== undefined,
  );
  return {
    scope: "historical-stability",
    validContractYears: new Set(validYears.map((estimate) => estimate.contractYear)).size,
    engleGranger,
    adf,
    beta: distribution(estimates.flatMap((estimate) => estimate.beta === undefined ? [] : [estimate.beta])),
    halfLife: distribution(estimates.flatMap((estimate) => estimate.halfLife === undefined ? [] : [estimate.halfLife])),
    negativeMeanReversionYears: {
      count: negative.rejected,
      evaluated: negative.evaluated,
      rate: negative.rate,
    },
  };
}

export interface EcmPoint {
  readonly date: ISODate;
  readonly residual: number;
}

export interface EcmEpisode {
  readonly pairPresetId: PairPresetId;
  readonly templateId: string;
  readonly contractYear: number;
  readonly actualContracts: ActualContractPair;
  readonly points: readonly EcmPoint[];
}

export interface PooledEcmRow {
  readonly contractYear: number;
  readonly date: ISODate;
  readonly delta: number;
  readonly laggedLevel: number;
  readonly laggedDeltas: readonly number[];
}

function validateEcmPanel(episodes: readonly EcmEpisode[]): void {
  const contractYears = new Set<number>();
  const reference = episodes[0];
  for (const episode of episodes) {
    if (contractYears.has(episode.contractYear)) {
      throw new Error(`Duplicate pooled ECM contract year: ${episode.contractYear}.`);
    }
    contractYears.add(episode.contractYear);
    const preset = getPairPreset(episode.pairPresetId);
    if (
      episode.actualContracts.pairId !== episode.pairPresetId ||
      episode.actualContracts.legA.instrumentId !== preset.legA ||
      episode.actualContracts.legB.instrumentId !== preset.legB
    ) {
      throw new Error("Pooled ECM actual contracts do not match their pair preset.");
    }
    const expectedTemplate = `${episode.actualContracts.legA.monthCode}/${episode.actualContracts.legB.monthCode}`;
    if (episode.templateId !== expectedTemplate) {
      throw new Error(`Pooled ECM templateId must identify its actual contracts (${expectedTemplate}).`);
    }
    if (
      episode.actualContracts.legA.year !== episode.contractYear ||
      episode.actualContracts.legB.year !== episode.contractYear
    ) {
      throw new Error("Pooled ECM contractYear must match both actual contract years.");
    }
    if (
      !preset.enabledCanolaMonths.includes(
        (preset.canolaLeg === "legA"
          ? episode.actualContracts.legA.monthCode
          : episode.actualContracts.legB.monthCode) as EnabledCanolaMonthCode,
      ) ||
      !preset.enabledComparatorMonths.includes(
        preset.canolaLeg === "legA"
          ? episode.actualContracts.legB.monthCode
          : episode.actualContracts.legA.monthCode,
      )
    ) {
      throw new Error("Pooled ECM actual contracts use a disabled template month.");
    }
    if (
      reference && (
        episode.pairPresetId !== reference.pairPresetId ||
        episode.templateId !== reference.templateId ||
        episode.actualContracts.legA.instrumentId !== reference.actualContracts.legA.instrumentId ||
        episode.actualContracts.legB.instrumentId !== reference.actualContracts.legB.instrumentId ||
        episode.actualContracts.legA.monthCode !== reference.actualContracts.legA.monthCode ||
        episode.actualContracts.legB.monthCode !== reference.actualContracts.legB.monthCode
      )
    ) {
      throw new Error("Pooled ECM cannot mix pair presets or contract templates.");
    }
  }
}

export function buildPooledEcmRows(
  episodes: readonly EcmEpisode[],
  lagOrder = 1,
): readonly PooledEcmRow[] {
  if (!Number.isInteger(lagOrder) || lagOrder < 0) throw new Error("lagOrder must be non-negative.");
  validateEcmPanel(episodes);
  return episodes.flatMap((episode) => {
    episode.points.forEach((point, index) => {
      parseISODate(point.date);
      if (!Number.isFinite(point.residual)) {
        throw new Error(`Pooled ECM contains a non-finite residual (${point.date}).`);
      }
      const previous = episode.points[index - 1];
      if (previous && compareISODate(previous.date, point.date) >= 0) {
        throw new Error(`Pooled ECM episode ${episode.contractYear} points must be strictly date-ordered.`);
      }
    });
    const points = episode.points;
    const deltas = points.map((point, index) =>
      index === 0 ? Number.NaN : point.residual - (points[index - 1]?.residual ?? point.residual),
    );
    const rows: PooledEcmRow[] = [];
    for (let index = lagOrder + 1; index < points.length; index += 1) {
      const point = points[index];
      const previous = points[index - 1];
      const delta = deltas[index];
      if (!point || !previous || delta === undefined) continue;
      const laggedDeltas = Array.from({ length: lagOrder }, (_, lag) => deltas[index - lag - 1] ?? Number.NaN);
      rows.push({
        contractYear: episode.contractYear,
        date: point.date,
        delta,
        laggedLevel: previous.residual,
        laggedDeltas,
      });
    }
    return rows;
  });
}

export interface PooledEcmEstimate {
  readonly gamma: number;
  readonly phi: number;
  readonly gammaClusteredStandardError: number;
  readonly gammaTStatistic: number;
  readonly halfLife: number;
  readonly contractYears: number;
  readonly episodeBoundaryDeltasExcluded: number;
  readonly lagOrder: number;
}

export function estimatePooledSeasonalEcm(
  episodes: readonly EcmEpisode[],
  options: {
    readonly lagOrder?: number;
    readonly minimumContractYears?: number;
    readonly minimumRows?: number;
    readonly stationaritySupported?: boolean;
  } = {},
): MetricResult<PooledEcmEstimate> {
  const lagOrder = options.lagOrder ?? 1;
  const rows = buildPooledEcmRows(episodes, lagOrder);
  const years = [...new Set(rows.map((row) => row.contractYear))].sort((a, b) => a - b);
  if (years.length < (options.minimumContractYears ?? 5) || rows.length < (options.minimumRows ?? 60)) {
    return notAvailable("insufficient-sample", "Pooled ECM requires more contract years and within-episode transitions.", rows.length);
  }
  if (options.stationaritySupported !== true) {
    return notAvailable("stationarity-not-supported", "Pooled half-life requires an explicit residual-stationarity gate.", rows.length);
  }
  const design = rows.map((row) => [
    1,
    row.laggedLevel,
    ...row.laggedDeltas,
    ...years.slice(1).map((year) => row.contractYear === year ? 1 : 0),
  ]);
  const fit = fitOls(rows.map((row) => row.delta), design);
  const clustered = fit ? clusterRobustStandardErrors(fit, design, rows.map((row) => row.contractYear)) : null;
  const gamma = fit?.coefficients[1];
  const gammaStandardError = clustered?.[1];
  if (!fit || gamma === undefined || gammaStandardError === undefined || gammaStandardError <= 0) {
    return notAvailable("unstable-estimate", "Pooled ECM fixed-effect regression is singular or cluster SE is unavailable.", rows.length);
  }
  const phi = 1 + gamma;
  const gammaTStatistic = gamma / gammaStandardError;
  if (phi <= 0 || phi >= 1) return notAvailable("half-life-not-defined", "Pooled ECM requires 0 < 1+gamma < 1.", rows.length);
  if (gammaTStatistic >= -1.645) return notAvailable("non-mean-reverting", "Pooled gamma is not significantly negative using contract-year clustered SE.", rows.length);
  const halfLife = -Math.log(2) / Math.log(phi);
  const maximumEpisodeLength = Math.max(...episodes.map((episode) => episode.points.length));
  if (!Number.isFinite(halfLife) || halfLife <= 0) return notAvailable("half-life-not-defined", "Pooled phi does not imply a valid positive half-life.", rows.length);
  if (halfLife > maximumEpisodeLength) return notAvailable("half-life-exceeds-window", "Pooled half-life exceeds the longest episode window.", rows.length);
  return available(
    {
      gamma,
      phi,
      gammaClusteredStandardError: gammaStandardError,
      gammaTStatistic,
      halfLife,
      contractYears: years.length,
      episodeBoundaryDeltasExcluded: Math.max(0, episodes.length - 1),
      lagOrder,
    },
    rows.length,
    "Seasonally adjusted pooled ECM with contract-year fixed effects and contract-year clustered SE; no boundary deltas",
  );
}
